import {
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { DisplayOnlyField } from "components/Forms/DisplayOnlyField";
import { ErrorDisplay } from "components/Forms/ErrorDisplay";
import { Issue } from "generated-graphql/graphql";
import { useValidatingFormContext } from "hooks/useValidatingForm";
import { useMemo } from "react";
import { Controller } from "react-hook-form";
import { Fragment } from "react/jsx-runtime";

import { groupIssuesByOption } from "./issueGrouping";
import { DocumentOption } from "./types";

type DocumentOptionsProps = {
  handleClose: () => void;
  handleNext: () => void;
  handleClearResponse: () => void;
  loading: boolean;
  /**
   * Issues to render under each radio option. Pass a stable snapshot rather
   * than a live validator stream so radio clicks don't make errors appear or
   * disappear as the user explores.
   */
  issues: Issue[];
  /**
   * Issues that pertain to the radio group itself rather than any specific
   * option. Rendered above the radios. Source these from live form issues so
   * the message clears as soon as the user makes a valid selection.
   */
  topBannerIssues: Issue[];
  /**
   * Per-radio errors only render under the radio matching this value, so
   * errors stay anchored to the saved state instead of following the user's
   * tentative radio clicks.
   */
  persistedDocumentOption: DocumentOption | null;
};

const RADIO_OPTIONS: { value: DocumentOption; label: string }[] = [
  { value: DocumentOption.Upload, label: "Upload Document(s)" },
  { value: DocumentOption.PublicInternetURL, label: "Public Internet URL" },
  { value: DocumentOption.ProvidedToRegulator, label: "Provided to Regulator" },
  { value: DocumentOption.ProvideElsewhere, label: "Provided Elsewhere in CERS" },
  { value: DocumentOption.Exempt, label: "Exempt" },
  { value: DocumentOption.StoredAtFacility, label: "Stored at Facility" },
];

export function DocumentOptions({
  handleClose,
  handleNext,
  handleClearResponse,
  loading,
  issues,
  topBannerIssues,
  persistedDocumentOption,
}: DocumentOptionsProps) {
  const theme = useTheme();
  const { control, watch, metadata } = useValidatingFormContext();
  const isDisplayOnly = metadata?.isDisplayOnly ?? false;

  const selectedOption: DocumentOption | null = watch("documentOption");
  const issuesByOption = useMemo(() => groupIssuesByOption(issues), [issues]);

  if (isDisplayOnly) {
    const text = RADIO_OPTIONS.find((o) => o.value === selectedOption)?.label;
    return (
      <Fragment>
        <DialogContent sx={{ paddingTop: 2 }}>
          <DisplayOnlyField text={text ?? ""} label="Document options" />
        </DialogContent>
        <DialogActions>
          <Stack
            direction="row"
            gap={theme.spacing(2)}
            justifyContent="flex-end"
            sx={{ marginTop: theme.spacing(2) }}
          >
            <Button onClick={handleClose} disabled={loading}>
              Done
            </Button>
          </Stack>
        </DialogActions>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <DialogContent sx={{ paddingTop: 2 }}>
        <FormControl fullWidth>
          <FormLabel id="documentOption-label">Document options</FormLabel>
          {topBannerIssues.map((issue, index) => (
            <ErrorDisplay
              key={`top-${issue.key}-${index}`}
              error={{ type: "required", message: issue.message }}
            />
          ))}
          <Controller
            name="documentOption"
            control={control}
            render={({ field }) => (
              <RadioGroup
                aria-labelledby="documentOption-label"
                {...field}
                value={field.value ?? ""}
                onChange={(e) => {
                  const next = RADIO_OPTIONS.find((option) => option.value === e.target.value);
                  field.onChange(next?.value ?? null);
                }}
              >
                <Stack direction="column" alignItems="flex-start">
                  {RADIO_OPTIONS.map((option) => {
                    const optionIssues =
                      persistedDocumentOption === option.value
                        ? (issuesByOption.get(option.value) ?? [])
                        : [];
                    return (
                      <Stack key={option.value} direction="column" alignSelf="stretch">
                        <FormControlLabel
                          control={<Radio />}
                          label={option.label}
                          value={option.value}
                        />
                        {optionIssues.map((issue, index) => (
                          <ErrorDisplay
                            key={`${issue.key}-${index}`}
                            error={{ type: "required", message: issue.message }}
                          />
                        ))}
                      </Stack>
                    );
                  })}
                </Stack>
              </RadioGroup>
            )}
          />
        </FormControl>

        <Typography variant="caption" marginBottom={theme.spacing(2)}>
          Please contact Encamp Support if you need another option for your document(s).
        </Typography>
      </DialogContent>
      <DialogActions>
        <Stack
          direction="row"
          gap={theme.spacing(2)}
          justifyContent="space-between"
          sx={{ marginTop: theme.spacing(2) }}
        >
          <Button onClick={handleClearResponse} variant="text" loading={loading}>
            Clear Response
          </Button>
          <Stack direction="row" gap={theme.spacing(2)}>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                if (!selectedOption) {
                  return;
                }
                handleNext();
              }}
              disabled={loading}
            >
              Next
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Fragment>
  );
}
